import React, { useState, useEffect } from 'react';
import { createBooking } from '../../http/bookingAPI';
import '../../styles/bookingForm.scss';
import { fetchCategories } from '../../http/categoryAPI';
import { fetchDoctors } from '../../http/doctorAPI';
import BookingDatePicker from './BookingDatePicker';
import BookingCreatedSuccessModal from './BookingCreatedSuccessModal';
import { i18n } from "@lingui/core";

const BookingForm = ({ onClose }) => {
  const [categories, setCategories] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [formData, setFormData] = useState({
    patientName: '',
    patientSurname: '',
    phone1: '',
    visitDate: '',
    age: null,
    categoryId: null,
    doctorId: null
  });
  const [errors, setErrors] = useState({});

  const validatePhone = (value) => {
    const regex = /^\+?[0-9\s-]{8,15}$/;
    return regex.test(value);
  };

  useEffect(() => {
    const getCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    getCategories();
  }, []);

  useEffect(() => {
    const getDoctors = async () => {
      try {
        const data = await fetchDoctors();
        setDoctors(data);
      } catch (error) {
        console.error('Error fetching doctors:', error);
      }
    };
    getDoctors();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value === "" ? null :
      (name === "categoryId" || name === "doctorId" || name === "age" ? Number(value) : value);

    setFormData((prev) => {
      let updated = { ...prev, [name]: newValue };

      // If category changes, reset doctorId
      if (name === "categoryId") {
        updated.doctorId = null;
      }

      // If doctor changes, auto-set categoryId
      if (name === "doctorId") {
        const selectedDoctor = doctors.find((doc) => doc.id === Number(value));
        if (selectedDoctor) {
          updated.categoryId = selectedDoctor.categoryId;
        }
      }

      return updated;
    });

    if (name === "phone1") {
      setErrors((prev) => ({
        ...prev,
        phone1: validatePhone(value) ? "" : "Invalid phone number format",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!errors.phone1 && validatePhone(formData.phone1)) {
      console.log("Form submitted:", formData);
    } else {
      alert("Please fix errors before submitting");
      return;
    }
    try {
      const response = await createBooking(formData);
      console.log('Server response:', response);
      setModalVisible(true);
      setFormData({
        patientName: '',
        patientSurname: '',
        phone1: '',
        visitDate: '',
        age: null,
        categoryId: null,
        doctorId: null
      });
      if (onClose) onClose();
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Something went wrong. Please try again.');
    }
  };

  // Filter doctors by selected category
  const filteredDoctors = formData.categoryId
    ? doctors.filter((doc) => doc.categoryId === formData.categoryId)
    : doctors;

  return (
    <div className="container mt-5">
      <form onSubmit={handleSubmit} className="booking-form">
        {/* Row 1: Name + Surname */}
        <div className="form-row booking-first-row">
          <div className="booking_input">
            <input
              type="text"
              name="patientName"
              value={formData.patientName}
              onChange={handleChange}
              required
              className="form-control"
              placeholder={i18n._("patientNamePlaceholder")}
            />
          </div>
          <div className="booking_input">
            <input
              type="text"
              name="patientSurname"
              value={formData.patientSurname}
              onChange={handleChange}
              required
              className="form-control"
              placeholder={i18n._("patientSurnamePlaceholder")}
            />
          </div>
        </div>

        {/* Row 2: Phone + DatePicker + Age */}
        <div className="form-row booking-second-row">
          <div className="booking_input">
            <input
              type="tel"
              name="phone1"
              value={formData.phone1}
              onChange={handleChange}
              required
              className={`form-control ${errors.phone1 ? "is-invalid" : ""}`}
              placeholder={i18n._("phone1Placeholder")}
            />
            {errors.phone1 && <div className="invalid-feedback">{errors.phone1}</div>}
          </div>
          <div className="booking_input">
            <BookingDatePicker formData={formData} setFormData={setFormData} />
          </div>
          <div className="booking_input">
            <input
              type="number"
              name="age"
              value={formData.age || ""}
              onChange={handleChange}
              className="form-control"
              placeholder={i18n._("agePlaceholder")}
              min={18}
            />
          </div>
        </div>

        {/* Row 3: Category + Doctor */}
        <div className="form-row booking-third-row">
          <div className="booking_input">
            <select
              name="categoryId"
              value={formData.categoryId || ""}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">{i18n._("categoryPlaceholder")}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {i18n.locale === "en"
                    ? category.nameEN
                    : i18n.locale === "ru"
                      ? category.nameRU
                      : i18n.locale === "am"
                        ? category.nameAM
                        : category.nameEN}
                </option>
              ))}
            </select>
          </div>
          <div className="booking_input">
            <select
              name="doctorId"
              value={formData.doctorId || ""}
              onChange={handleChange}
              className="form-control"
              disabled={!formData.categoryId && doctors.length > 0} // optional: disable until category chosen
            >
              <option value="">{i18n._("doctorPlaceholder")}</option>
              {filteredDoctors.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {i18n.locale === "en"
                    ? doctor.firstNameEN + " " + doctor.lastNameEN
                    : i18n.locale === "ru"
                      ? doctor.firstNameRU + " " + doctor.lastNameRU
                      : i18n.locale === "am"
                        ? doctor.firstNameAM + " " + doctor.lastNameAM
                        : doctor.firstNameEN + " " + doctor.lastNameEN}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Submit button */}
        <div className="form-row center">
          <button type="submit" className="booking-button">
            <span>{i18n._("bookButton")}</span>
          </button>
        </div>
      </form>

      <BookingCreatedSuccessModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
      />
    </div>
  );
};

export default BookingForm;
